import { useEffect } from 'react';
import isEqual from 'lodash/isEqual';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { isCustomUI } from '../../../utils/isCustomUI';
import type { ChangeListener } from '../../../bridge/BridgeContextObject';
import type { InnerIframeProps } from '../types';

const MACRO_MODULE_NAME = 'xen:macro';

type UseReloadOnMacroConfigChangeInput = {
	extension: InnerIframeProps['extension'];
	extensionData: InnerIframeProps['extensionData'];
	extensionPayload: InnerIframeProps['extensionPayload'];
	onChange: (listener: ChangeListener) => () => void;
	reloadIframe: () => void;
};

// True in config mode only. Both locations are read because
// `forge-ui-move-macro-data-to-extension-data` moves macro data from extensionPayload to extensionData.
const getIsConfiguringMacro = ({
	extensionData,
	extensionPayload,
}: Pick<UseReloadOnMacroConfigChangeInput, 'extensionData' | 'extensionPayload'>): boolean =>
	Boolean(extensionPayload?.macro?.isConfiguring || extensionData?.macro?.isConfiguring);

/**
 * TODO: temporary workaround — delete this file and its call site once every macro observes config
 * changes through the event-based API (e.g. useConfig). Then no reload is wanted at all.
 *
 * Reloads the main macro only: UI Kit receives config through the host renderer, and the config
 * experience is the source of the change — reloading it would discard the form being edited.
 *
 * Observes this instance's own context, not FORGE_CORE_MACRO_CONFIG_CHANGED — that event is scoped
 * to app + environment, so it would reload every instance of the app on the page.
 */
export const useReloadOnMacroConfigChange = ({
	extension,
	extensionData,
	extensionPayload,
	onChange,
	reloadIframe,
}: UseReloadOnMacroConfigChangeInput): void => {
	const isReloadableCustomUIMacro =
		extension.type === MACRO_MODULE_NAME &&
		isCustomUI(extension) &&
		!getIsConfiguringMacro({ extensionData, extensionPayload });

	useEffect(() => {
		if (!fg('forge-ui-bridge-v2-macro-config-reload') || !isReloadableCustomUIMacro) {
			return;
		}
		return onChange((prev, next, changes) => {
			// extensionPayloadChanges is a reference pre-filter only — verify the value changed.
			if (!changes.extensionPayloadChanges.config) {
				return;
			}
			if (isEqual(prev.extensionPayload?.config, next.extensionPayload?.config)) {
				return;
			}
			reloadIframe();
		});
	}, [isReloadableCustomUIMacro, onChange, reloadIframe]);
};
